import {
  faPlus,
  faFilter,
  faCalendarAlt,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import Button from "components/base/Button";
import PageBreadcrumb from "components/common/PageBreadcrumb";
import SearchBox from "components/common/SearchBox";
import ToastNotification from "components/common/ToastNotification";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import { ChangeEvent, useEffect, useState } from "react";
import { Spinner, Card, Badge, Row, Col, Form } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { usePermissionModal } from "contexts/PermissionModalContext";
import PromocaoTable, {
  promocaoTableColumns,
} from "components/tables/PromocaoTable";
import { PromocaoTableType, promocoesBreadcrumbItems } from "data/promocoes";

// Import ResultResponse type from data/produto
import { ResultResponse } from "data/produto";

// Define o tipo específico para a resposta de promoções
type PromocaoApiResponse = PromocaoTableType[];

const fetchPromocoes = async (termo?: string, somenteAtivas?: boolean) => {
  const response = await axios.get<ResultResponse<PromocaoApiResponse>>("/api/promocao", {
    params: {
      ...(termo && { termo }),
      ...(somenteAtivas !== undefined && { somenteAtivas }),
    },
  });
  return response.data;
};

const Promocoes = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Add state for success toast
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  
  // Use the permission modal hook
  const { getQueryRetryHandler } = usePermissionModal();

  const location = useLocation();
  const navigate = useNavigate();

  // Check URL parameters for success message
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const successCode = params.get("successCode");
    const isNew = params.get("new") === "true";

    if (successCode) {
      const action = isNew ? "criada" : "atualizada";
      setSuccessMessage(
        `Promoção de código <b>${successCode}</b> foi ${action} com sucesso!`
      );
      setShowSuccessToast(true);

      // Clear the URL params without refreshing the page
      navigate("/cadastros/promocoes", { replace: true });
    }
  }, [location, navigate]);

  const dataQuery = useQuery<ResultResponse<PromocaoApiResponse>>({
    queryKey: ["promocoes", searchTerm],
    queryFn: () => fetchPromocoes(searchTerm),
    placeholderData: keepPreviousData,
    retry: getQueryRetryHandler({
      queryKey: ["promocoes", searchTerm],
      defaultMessage: "Você não possui permissão para consultar promoções."
    }),
  });

  const table = useReactTable({
    data: dataQuery.data?.data ?? [],
    columns: promocaoTableColumns,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (dataQuery.isLoading)
    return (
      <Spinner animation="border" role="status" className="d-block mx-auto">
        <span className="visually-hidden">Carregando...</span>
      </Spinner>
    );

  // Check for access denied (403 error)
  if (dataQuery.error) {
    const error = dataQuery.error as any;
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      return (
        <div>
          <PageBreadcrumb items={promocoesBreadcrumbItems} />
          <div className="mb-9">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Promoções</h2>
              <Button
                variant="primary"
                as={Link}
                to="/cadastros/promocoes/novo"
                className="px-4 py-2"
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Nova Promoção
              </Button>
            </div>

            <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
              <div className="d-flex gap-2 flex-grow-1">
                <SearchBox
                  placeholder="Pesquisar promoções por código ou descrição..."                
                  className="flex-grow-1"
                  disabled={true}
                />
                <Button
                  variant="phoenix-primary"                
                  disabled={true}
                >
                  Atualizar
                </Button>
              </div>
            </div>

            <AccessDeniedOverlay message="Você não tem permissão para consultar ou visualizar promoções.">
              <Card
                className="border-0 shadow-sm"
                style={{
                  filter: "blur(8px)",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              >
                <Card.Body className="p-0">
                  <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Código</th>
                            <th>Descrição</th>
                            <th>Data Início</th>
                            <th>Data Fim</th>
                            <th>Status</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array(5)
                            .fill(0)
                            .map((_, i) => (
                              <tr key={i}>
                                <td>{i + 100}</td>
                                <td>Promoção Exemplo</td>
                                <td>01/01/2023</td>
                                <td>31/12/2023</td>
                                <td>Ativa</td>
                                <td>-</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </AccessDeniedOverlay>
          </div>
        </div>
      );
    }

    return (
      <div className="alert alert-danger">
        Erro ao carregar promoções:{" "}
        {(dataQuery.error as Error).message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  // Display error message if isSuccess is false - using Result pattern
  if (dataQuery.data && dataQuery.data.isSuccess === false) {
    return (
      <div className="alert alert-danger">
        Erro ao carregar promoções:{" "}
        {dataQuery.data.message || "Ocorreu um erro desconhecido."}
      </div>
    );
  }

  // Para debug - verifica estrutura dos dados
  console.log("Estado da query de promoções:", {
    isLoading: dataQuery.isLoading,
    isError: dataQuery.isError,
    data: dataQuery.data
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    dataQuery.refetch();
  };

  return (
    <div>
      <PageBreadcrumb items={promocoesBreadcrumbItems} />
      <div className="mb-9">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Promoções</h2>
          <Button
            variant="primary"
            as={Link}
            to="/cadastros/promocoes/novo"
            className="px-4 py-2"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Nova Promoção
          </Button>
        </div>

        <ToastNotification
          show={showSuccessToast}
          type="success"
          message={successMessage}
          onClose={() => setShowSuccessToast(false)}
          dangerouslySetInnerHTML={true}
        />

        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2 flex-grow-1">
            <SearchBox
              placeholder="Pesquisar promoções por código ou descrição..."
              onChange={handleSearchInputChange}
              className="flex-grow-1"
            />
            <Button variant="phoenix-primary" onClick={handleRefresh}>
              Atualizar
            </Button>
          </div>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <AdvanceTableProvider {...table}>
              <div className="mx-n4 px-4 rounded-4 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
                <PromocaoTable />
              </div>
            </AdvanceTableProvider>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Promocoes;
